import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

/** UI role a member can be assigned (Owner is transferred separately, not set here). */
export type AssignableMemberRole = 'admin' | 'edit' | 'view'

/**
 * CLUB-174: persist an Administrator's change to a member's role via the REST
 * BFF (PATCH /clubhouse/groups/{groupId}/members/{memberId}), then refresh the
 * members list so every surface reflects the new role.
 *
 * The UI role ids ('admin' | 'edit' | 'view') match the backend role strings, so
 * they're sent through as-is. Owner-only enforcement (who may change roles) is
 * the backend's responsibility (CLUB-175) — the UI additionally hides the control
 * from non-admins.
 */
export function useUpdateMemberRole(groupId: string) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: AssignableMemberRole }) =>
      call<void>(`/clubhouse/groups/${groupId}/members/${encodeURIComponent(memberId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', groupId, 'members'] })
      // The members list is not the only thing a role change moves: my-groups
      // carries the caller's own role, which becomes `Team.canEdit`. Without
      // this, an administrator who demotes themselves keeps every editing
      // affordance until that query goes stale on its own — the members list
      // would show the new role while the app still behaved as the old one.
      // Every sibling mutation here (create, rename, delete, leave, join code)
      // already invalidates this key.
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
